import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import {
    ActionIcon,
    Badge,
    Box,
    Center,
    Checkbox,
    Collapse,
    Group,
    Loader,
    Pagination,
    Paper,
    Select,
    Stack,
    Text,
    Tooltip,
    UnstyledButton,
} from '@mantine/core';
import {
    IconAlertTriangle,
    IconCamera,
    IconChevronDown,
    IconChevronUp,
    IconEye,
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import { DataTable, DataTableProps } from 'mantine-datatable';
import { useQuery } from '@apollo/client';

import { FilterInput, Request } from '../../../codegen/graphql';
import {
    getPricingBatchesColumns,
    PricingBatchesColumnsOptions,
} from '../columns/pricingBatchesColumns';
import type { ColumnBuilderCtx } from '../BaseRequestsTable';
import { SimpleHeader } from '../components/TableHeaderWrapper';
import { commonTableStyles } from '../../../constants/tableStyles';
import { PRICING_BATCH_INNER_TABLE_SELECTION_COLUMN_WIDTH_PX } from '../../../constants/pricingBatchTableColumnWidths';
import { PropagationStopper, When } from '../../common';
import { BATCH_STAT_COLUMN_WIDTHS, StatPill } from './StatPill';
import { SkuHeader } from '../components/filter-columns/SkuHeader';
import { ProductCategoryHeader } from '../components/filter-columns/ProductCategoryHeader';
import { StoreColumnHeader } from '../components/filter-columns/StoreColumnHeader';
import { GET_PRICE_CHANGE_REQUESTS_FOR_BATCH } from '../../../apollo/queries/priceChangeRequest';
import { showErrorNotification } from '../../../notifications';
import {
    getAdvertisingZoneMantineColor,
    getAdvertisingZoneShortLabel,
} from '../../../helpers/advertisingZoneDisplay';
import {
    BATCH_URGENT_TOOLTIP_LABEL,
    getBatchDueIconState,
} from '../../../helpers/batchDueUrgency';
import { useBatchExpansion } from './BatchExpansionContext';
import { BatchSalesScopeProvider } from './BatchSalesScopeContext';
import { BatchExpansionPortal } from './BatchExpansionPortal';
import { BatchRowExpansionContent } from './BatchRowExpansionContent';
import { BatchCardCompetitorBlock } from './BatchCardCompetitorBlock';
import type { BatchCardProps } from './BatchCard.types';
import { useAppConfig } from '../../../hooks/useAppConfig';
import { useUser } from '../../../hooks/useUser';

function batchProofBadgeLabel(
    proofCount: number,
    canOpenViewer: boolean,
): string {
    if (proofCount === 0) {
        return 'No attachments';
    }
    if (canOpenViewer && proofCount === 1) {
        return 'View 1 attachment';
    }
    if (canOpenViewer) {
        return `View ${proofCount} attachments`;
    }
    if (proofCount === 1) {
        return '1 attachment';
    }
    return `${proofCount} attachments`;
}

const INNER_HIDE_COLUMNS: PricingBatchesColumnsOptions = {
    hideColumns: ['friendlyId', 'advertisingZone', 'priceDirection'],
};

export function BatchCardInner({
    batch,
    baseFilter,
    openRequestDrawer,
    selected,
    onSelectedChange,
    selectedRowIds,
    onSelectedRowIdsChange,
    onRowClick,
    onOpenBatchProofViewer,
    onCompetitorClick,
    defaultOpen = false,
    enableInnerTableFilters = true,
    paginateInnerTable = true,
}: BatchCardProps) {
    const expansion = useBatchExpansion();
    const { vatPercentage } = useAppConfig();
    const { isStoreManager } = useUser();

    const paperRef = useRef<HTMLDivElement>(null);
    const batchTableScrollViewportRef = useRef<HTMLDivElement>(null);

    const [open, setOpen] = useState(defaultOpen);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const [localSku, setLocalSku] = useState<string[] | null>(null);
    const [localCategory, setLocalCategory] = useState<string[] | null>(null);
    const [localStoreIds, setLocalStoreIds] = useState<string[] | null>(null);
    const [localOpsAreaIds, setLocalOpsAreaIds] = useState<string[] | null>(
        null,
    );
    const [localDivisionIds, setLocalDivisionIds] = useState<string[] | null>(
        null,
    );

    const hasFilters = [
        localSku,
        localCategory,
        localStoreIds,
        localOpsAreaIds,
        localDivisionIds,
    ].some(values => (values?.length ?? 0) > 0);

    const cardFilter = useMemo<FilterInput>(
        () => ({
            ...baseFilter,
            productSku: localSku && localSku.length > 0 ? localSku[0] : null,
            productCategory: localCategory,
            storeIds: localStoreIds,
            operationsAreaIds: localOpsAreaIds,
            divisionIds: localDivisionIds,
            friendlyId: null,
            take: undefined,
            skip: undefined,
        }),
        [
            baseFilter,
            localSku,
            localCategory,
            localStoreIds,
            localOpsAreaIds,
            localDivisionIds,
        ],
    );

    const {
        data: cardData,
        loading: cardLoading,
        error: cardError,
    } = useQuery(GET_PRICE_CHANGE_REQUESTS_FOR_BATCH, {
        fetchPolicy: 'cache-and-network',
        skip: !hasFilters,
        variables: { batchId: batch.batchId, input: cardFilter },
    });

    const effectiveRequests = useMemo<Request[]>(() => {
        if (!hasFilters) {
            return batch.requests;
        }
        return cardData?.getPriceChangeRequestsForBatch ?? [];
    }, [hasFilters, batch.requests, cardData]);

    useEffect(() => {
        if (cardError) {
            showErrorNotification('Error loading filtered batch requests');
        }
    }, [cardError]);

    useEffect(() => {
        if (!paginateInnerTable) {
            return;
        }
        setPage(1);
    }, [pageSize, effectiveRequests.length, hasFilters, paginateInnerTable]);

    const localColumnCtx = useMemo<ColumnBuilderCtx>(() => {
        const pricingBatchIndicators = {
            vatPercentage,
            hideCostAndMargins: isStoreManager,
        };
        if (!enableInnerTableFilters) {
            return {
                openRequestDrawer,
                pricingBatchIndicators,
                headers: {
                    FriendlyId: null,
                    Sku: <SimpleHeader text="SKU" />,
                    ProductCategory: <SimpleHeader text="Product Category" />,
                    Store: <SimpleHeader text="Store" />,
                    AdvertisingZone: null,
                    Status: null,
                    SapOutcome: null,
                    ProcurementManager: null,
                    Survey: null,
                    Scope: null,
                },
            };
        }
        return {
            openRequestDrawer,
            pricingBatchIndicators,
            headers: {
                FriendlyId: null,
                Sku: (
                    <SkuHeader currentValue={localSku} onChange={setLocalSku} />
                ),
                ProductCategory: (
                    <ProductCategoryHeader
                        currentValue={localCategory}
                        onChange={setLocalCategory}
                    />
                ),
                Store: (
                    <StoreColumnHeader
                        currentOperationsAreaIds={localOpsAreaIds}
                        currentDivisionIds={localDivisionIds}
                        currentStoreIds={localStoreIds}
                        onChange={(ops, div, stores) => {
                            setLocalOpsAreaIds(ops);
                            setLocalDivisionIds(div);
                            setLocalStoreIds(stores);
                        }}
                    />
                ),
                AdvertisingZone: null,
                Status: null,
                SapOutcome: null,
                ProcurementManager: null,
                Survey: null,
                Scope: null,
            },
        };
    }, [
        enableInnerTableFilters,
        openRequestDrawer,
        localSku,
        localCategory,
        localOpsAreaIds,
        localDivisionIds,
        localStoreIds,
        vatPercentage,
        isStoreManager,
    ]);

    const columns = useMemo(
        () =>
            getPricingBatchesColumns(localColumnCtx, {
                ...INNER_HIDE_COLUMNS,
                rowIndexOffset: paginateInnerTable ? (page - 1) * pageSize : 0,
                uniformColumnWidths: true,
            }),
        [localColumnCtx, page, pageSize, paginateInnerTable],
    );

    const pagedRequests = useMemo(
        () =>
            paginateInnerTable
                ? effectiveRequests.slice(
                      (page - 1) * pageSize,
                      page * pageSize,
                  )
                : effectiveRequests,
        [effectiveRequests, page, pageSize, paginateInnerTable],
    );

    const pagedSelectedRecords = useMemo(
        () => pagedRequests.filter(r => selectedRowIds.includes(r.id)),
        [pagedRequests, selectedRowIds],
    );

    const totalRecords = effectiveRequests.length;
    const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));
    const rangeStart = totalRecords === 0 ? 0 : (page - 1) * pageSize + 1;
    const rangeEnd = Math.min(page * pageSize, totalRecords);

    const zoneName = batch.advertisingZone?.name;
    const zoneLabel = zoneName ? `Zone ${zoneName}` : 'Unassigned zone';
    const zoneShortLabel = getAdvertisingZoneShortLabel(zoneName);
    const zoneMantineColor = getAdvertisingZoneMantineColor(zoneName);
    const { competitor } = batch;
    const competitorName = competitor?.name ?? 'N/A';
    const proofCount = batch.proofOfCompetitorPricing?.length ?? 0;
    const earliest = batch.earliestCreatedAt
        ? dayjs(batch.earliestCreatedAt).format('YYYY-MM-DD')
        : '—';

    const batchDueIcon = useMemo(
        () => getBatchDueIconState(batch.requests),
        [batch.requests],
    );

    const zoneBlockTooltip = zoneName?.trim()
        ? `${zoneName.trim()} · ${batchDueIcon.tooltipLabel}`
        : `Unassigned zone · ${batchDueIcon.tooltipLabel}`;

    const relevantRequestIds = useMemo(
        () => new Set(effectiveRequests.map(r => r.id)),
        [effectiveRequests],
    );

    const selectedInCurrentView = useMemo(
        () => selectedRowIds.filter(id => relevantRequestIds.has(id)).length,
        [selectedRowIds, relevantRequestIds],
    );

    const allVisibleRowsSelected =
        effectiveRequests.length > 0 &&
        selectedInCurrentView === effectiveRequests.length;

    const hasPartialRowSelection =
        !selected &&
        selectedInCurrentView > 0 &&
        selectedInCurrentView < effectiveRequests.length;

    const hasNoFilteredResults =
        hasFilters &&
        !cardLoading &&
        !cardError &&
        effectiveRequests.length === 0;

    const handleCompetitorPillClick = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            onCompetitorClick?.(batch);
        },
        [batch, onCompetitorClick],
    );

    const handleProofSectionClick = useCallback(() => {
        if ((batch.proofOfCompetitorPricing?.length ?? 0) === 0) {
            return;
        }
        onOpenBatchProofViewer?.(batch);
    }, [batch, onOpenBatchProofViewer]);

    const proofBadgeLabel = batchProofBadgeLabel(
        proofCount,
        Boolean(onOpenBatchProofViewer),
    );

    const handleExpandIconClick = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        setOpen(o => !o);
    }, []);

    const handleSelectedRecordsChange = useCallback(
        (records: Request[]) => {
            const pagedIdSet = new Set(pagedRequests.map(r => r.id));
            const offPage = selectedRowIds.filter(id => !pagedIdSet.has(id));
            onSelectedRowIdsChange([...offPage, ...records.map(r => r.id)]);
        },
        [pagedRequests, selectedRowIds, onSelectedRowIdsChange],
    );

    const handleDataTableRowClick = useCallback(
        ({ record }: { record: Request }) => {
            onRowClick?.(record);
        },
        [onRowClick],
    );

    const renderBatchRowExpansionContent = useCallback(
        ({ record, collapse }: { record: Request; collapse: () => void }) => (
            <BatchExpansionPortal anchorRef={paperRef}>
                <BatchRowExpansionContent
                    record={record}
                    collapse={collapse}
                    getKindFor={expansion.getKindFor}
                    closeExpansion={expansion.close}
                />
            </BatchExpansionPortal>
        ),
        [expansion.close, expansion.getKindFor],
    );

    const proofRow = (
        <Group gap="sm" align="center" wrap="nowrap">
            <IconCamera
                size={18}
                className="text-primary shrink-0"
                stroke={1.75}
            />
            <Badge
                size="md"
                radius="xl"
                variant="light"
                className="!bg-blue-100 !text-blue-500 border-0 max-w-full shrink min-w-0"
                classNames={{
                    label: 'whitespace-normal text-left leading-snug font-semibold normal-case overflow-visible max-w-full',
                }}
            >
                {proofBadgeLabel}
            </Badge>
        </Group>
    );

    const competitorStatPill = (
        <StatPill
            label="Competitor"
            value={competitorName}
            truncateValue={false}
            trailing={
                <IconEye
                    size={16}
                    className="shrink-0 text-primary"
                    stroke={1.75}
                />
            }
            onClick={onCompetitorClick ? handleCompetitorPillClick : undefined}
        />
    );

    return (
        <BatchSalesScopeProvider
            requests={batch.requests}
            advertisingZoneId={batch.advertisingZone?.id}
        >
            <Paper
                ref={paperRef}
                withBorder
                shadow="sm"
                radius="lg"
                className="relative"
            >
                <Group align="flex-start" gap="sm" p="md" wrap="nowrap">
                    <Box
                        pt={4}
                        onClick={e => e.stopPropagation()}
                        role="presentation"
                    >
                        <Checkbox
                            checked={selected || allVisibleRowsSelected}
                            indeterminate={hasPartialRowSelection}
                            onChange={event =>
                                onSelectedChange(event.currentTarget.checked)
                            }
                            aria-label={`Select batch ${zoneLabel}`}
                        />
                    </Box>

                    <UnstyledButton
                        onClick={() => setOpen(o => !o)}
                        className="flex-1 text-left"
                        aria-expanded={open}
                    >
                        <Group
                            align="flex-start"
                            gap="md"
                            wrap="nowrap"
                            w="100%"
                        >
                            <Group
                                align="center"
                                gap="md"
                                wrap="nowrap"
                                className="w-fit max-w-full shrink-0"
                            >
                                <Tooltip
                                    label={zoneBlockTooltip}
                                    multiline
                                    openDelay={250}
                                    withinPortal
                                    position="top"
                                    zIndex={400}
                                    maw={{
                                        base: 'min(420px,calc(100vw - 1.5rem))',
                                    }}
                                    middlewares={{
                                        flip: true,
                                        shift: { padding: 12 },
                                        inline: false,
                                    }}
                                    classNames={{
                                        tooltip:
                                            'bg-[#4D5E80] whitespace-normal break-words text-left text-xs leading-snug text-white rounded-md px-2 py-1.5 shadow-md',
                                    }}
                                >
                                    <Badge
                                        variant="light"
                                        color={zoneMantineColor}
                                        size="lg"
                                        radius="md"
                                        className="h-10 w-10 shrink-0 p-0 font-bold tabular-nums normal-case"
                                        classNames={{
                                            label: 'flex h-full w-full items-center justify-center px-0.5 text-sm leading-none',
                                        }}
                                        aria-label={zoneBlockTooltip}
                                    >
                                        {zoneShortLabel}
                                    </Badge>
                                </Tooltip>
                                <Stack gap={2} align="flex-start">
                                    <Text
                                        fw={700}
                                        size="md"
                                        className="truncate"
                                    >
                                        {zoneLabel}
                                    </Text>
                                    <Text size="xs" c="dimmed">
                                        {earliest}
                                    </Text>
                                </Stack>
                                <When
                                    condition={
                                        batchDueIcon.urgency === 'critical'
                                    }
                                >
                                    <Tooltip
                                        label={BATCH_URGENT_TOOLTIP_LABEL}
                                        openDelay={100}
                                        withinPortal
                                        position="top"
                                        zIndex={400}
                                        classNames={{
                                            tooltip:
                                                'bg-[#4D5E80] text-white rounded-md text-xs px-2 py-1',
                                        }}
                                    >
                                        <Badge
                                            color="red"
                                            variant="light"
                                            size="md"
                                            className="normal-case shrink-0"
                                            leftSection={
                                                <IconAlertTriangle
                                                    size={16}
                                                    stroke={2}
                                                />
                                            }
                                            aria-label={
                                                BATCH_URGENT_TOOLTIP_LABEL
                                            }
                                        >
                                            Urgent
                                        </Badge>
                                    </Tooltip>
                                </When>
                            </Group>
                            <Group
                                gap="md"
                                wrap="wrap"
                                justify="flex-end"
                                align="flex-start"
                                className="ml-auto"
                            >
                                <StatPill
                                    label="SKUs"
                                    value={batch.requestCount}
                                    metric
                                    width={BATCH_STAT_COLUMN_WIDTHS.metric}
                                />
                                <Box className="min-w-[220px] max-w-[18rem] flex-[1_1_220px]">
                                    <BatchCardCompetitorBlock
                                        competitor={batch.competitor}
                                        statPill={competitorStatPill}
                                    />
                                </Box>
                                <PropagationStopper className="min-w-0 shrink">
                                    <Box
                                        px="sm"
                                        py={4}
                                        w={BATCH_STAT_COLUMN_WIDTHS.proof}
                                        className="min-w-0"
                                    >
                                        <Stack gap={6} align="flex-start">
                                            <Text
                                                size="xs"
                                                c="dimmed"
                                                className="uppercase tracking-wider"
                                            >
                                                Proof of Pricing
                                            </Text>
                                            <When
                                                condition={
                                                    proofCount > 0 &&
                                                    !!onOpenBatchProofViewer
                                                }
                                            >
                                                <UnstyledButton
                                                    type="button"
                                                    onClick={
                                                        handleProofSectionClick
                                                    }
                                                    className="cursor-pointer rounded-md hover:bg-gray-50/80 [&_*]:cursor-pointer"
                                                    aria-label={proofBadgeLabel}
                                                >
                                                    {proofRow}
                                                </UnstyledButton>
                                            </When>
                                            <When
                                                condition={
                                                    proofCount === 0 ||
                                                    !onOpenBatchProofViewer
                                                }
                                            >
                                                <Box
                                                    aria-label="No proof files for this batch"
                                                    role="presentation"
                                                >
                                                    {proofRow}
                                                </Box>
                                            </When>
                                        </Stack>
                                    </Box>
                                </PropagationStopper>
                                <Box className="shrink-0 self-center">
                                    <Tooltip
                                        label={open ? 'Collapse' : 'Expand'}
                                        openDelay={150}
                                    >
                                        <ActionIcon
                                            variant="subtle"
                                            color="gray"
                                            aria-label={
                                                open
                                                    ? 'Collapse batch'
                                                    : 'Expand batch'
                                            }
                                            onClick={handleExpandIconClick}
                                        >
                                            <When
                                                condition={open}
                                                fallback={
                                                    <IconChevronDown
                                                        size={18}
                                                    />
                                                }
                                            >
                                                <IconChevronUp size={18} />
                                            </When>
                                        </ActionIcon>
                                    </Tooltip>
                                </Box>
                            </Group>
                        </Group>
                    </UnstyledButton>
                </Group>

                <Collapse in={open}>
                    <Box
                        className="border-t border-gray-200 overflow-x-hidden"
                        px="md"
                        pb="md"
                        pt="sm"
                    >
                        <When condition={hasFilters && cardLoading}>
                            <Center py="xl">
                                <Loader size="sm" />
                            </Center>
                        </When>
                        <When
                            condition={
                                hasFilters && !!cardError && !cardLoading
                            }
                        >
                            <Center py="xl">
                                <Text size="sm" c="red" ta="center">
                                    Could not load filtered requests. Adjust
                                    filters or try again.
                                </Text>
                            </Center>
                        </When>
                        <When
                            condition={
                                !(hasFilters && cardLoading) &&
                                !(hasFilters && !!cardError && !cardLoading)
                            }
                        >
                            <DataTable
                                {...({
                                    scrollViewportRef:
                                        batchTableScrollViewportRef,
                                    withTableBorder: false,
                                    striped: true,
                                    verticalSpacing: 'xs',
                                    fz: 'sm',
                                    withColumnBorders: false,
                                    noRecordsText: hasNoFilteredResults
                                        ? 'No requests match this filter'
                                        : 'No requests in this batch',
                                    highlightOnHover: false,
                                    records: pagedRequests,
                                    columns,
                                    idAccessor: 'id',
                                    selectedRecords: pagedSelectedRecords,
                                    onSelectedRecordsChange:
                                        handleSelectedRecordsChange,
                                    selectionColumnStyle: {
                                        width: PRICING_BATCH_INNER_TABLE_SELECTION_COLUMN_WIDTH_PX,
                                        minWidth:
                                            PRICING_BATCH_INNER_TABLE_SELECTION_COLUMN_WIDTH_PX,
                                        maxWidth:
                                            PRICING_BATCH_INNER_TABLE_SELECTION_COLUMN_WIDTH_PX,
                                    },
                                    selectionColumnClassName:
                                        'sticky left-0 z-[25] !bg-white shadow-right',
                                    onRowClick: onRowClick
                                        ? handleDataTableRowClick
                                        : undefined,
                                    rowClassName: ({ id }: Request) =>
                                        selectedRowIds.includes(id)
                                            ? 'bg-blue-50 hover:bg-blue-100'
                                            : 'bg-white hover:bg-blue-50 cursor-pointer',
                                    classNames: {
                                        table: 'selection-column-fix !table-fixed !w-max !min-w-max [&_th]:align-middle [&_td]:align-middle',
                                    },
                                    styles: commonTableStyles,
                                    rowExpansion: {
                                        allowMultiple: false,
                                        trigger: 'never',
                                        expanded: {
                                            recordIds:
                                                expansion.expandedRecordIds,
                                            onRecordIdsChange: ids => {
                                                if (ids.length === 0) {
                                                    expansion.closeAll();
                                                }
                                            },
                                        },
                                        collapseProps: {
                                            transitionDuration: 250,
                                            animateOpacity: false,
                                            transitionTimingFunction:
                                                'ease-out',
                                        },
                                        content: renderBatchRowExpansionContent,
                                    },
                                    scrollAreaProps: {
                                        viewportProps: {
                                            className:
                                                'min-w-0 [container-type:inline-size]',
                                        },
                                    },
                                } as DataTableProps<Request>)}
                            />
                        </When>
                        <When condition={paginateInnerTable}>
                            <Group justify="flex-end" gap="md" w="100%" pt="sm">
                                <Select
                                    data={['10', '20', '50', '100']}
                                    value={pageSize.toString()}
                                    onChange={value => {
                                        if (value) {
                                            setPageSize(parseInt(value, 10));
                                        }
                                    }}
                                    size="xs"
                                    className="w-20"
                                    aria-label="Rows per page"
                                />
                                <Pagination
                                    value={page}
                                    total={totalPages}
                                    onChange={setPage}
                                    size="sm"
                                />
                                <Text size="xs" c="dimmed" ml="xs">
                                    <When
                                        condition={totalRecords === 0}
                                        fallback={`${rangeStart}-${rangeEnd} of ${totalRecords}`}
                                    >
                                        0 of 0
                                    </When>
                                </Text>
                            </Group>
                        </When>
                    </Box>
                </Collapse>
            </Paper>
        </BatchSalesScopeProvider>
    );
}
